export const CONSENT_CATEGORIES = Object.freeze(["NECESSARY", "FUNCTIONAL", "ANALYTICS", "ADVERTISING", "PERSONALIZATION"]);
export const OPTIONAL_CONSENT_CATEGORIES = Object.freeze(CONSENT_CATEGORIES.filter(category => category !== "NECESSARY"));
export const CONSENT_VALUES = Object.freeze(["GRANTED", "DENIED", "UNSET"]);
export const CONSENT_PROFILES = Object.freeze(["STRICT_OPT_IN", "OPT_OUT", "INFORMATIONAL_ONLY"]);
// Match the existing first-party consent cookie's approved 180-day lifetime.
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

export function validConsentTimestamp(timestamp, now = Date.now()) {
  if (typeof timestamp !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(timestamp)) return false;
  const parsed = Date.parse(timestamp);
  if (!Number.isFinite(parsed) || !Number.isFinite(now)) return false;
  const normalized = timestamp.includes(".") ? timestamp : timestamp.replace("Z", ".000Z");
  return new Date(parsed).toISOString() === normalized && parsed <= now && now - parsed < CONSENT_COOKIE_MAX_AGE_SECONDS * 1000;
}

export function hasCurrentAnalyticsConsent(config, stored, publicationId, now = Date.now()) {
  if (config?.enabled !== true || !config.policyVersion || !validConsentTimestamp(stored?.timestamp, now)) return false;
  return advertisingConsentContract(resolveConsentState(config, stored, { publicationId })).analyticsAllowed;
}

export function hasCurrentAdvertisingConsent(config, stored, publicationId, now = Date.now()) {
  if (config?.enabled !== true || !config.policyVersion || !validConsentTimestamp(stored?.timestamp, now)) return false;
  return advertisingConsentContract(resolveConsentState(config, stored, { publicationId })).advertisingAllowed;
}

export const CATEGORY_DESCRIPTIONS = Object.freeze({
  NECESSARY: "Required for security, publication routing, and core site operation.",
  FUNCTIONAL: "Supports optional site features and enhanced functionality.",
  ANALYTICS: "Helps the publication understand aggregate site use and performance.",
  ADVERTISING: "Allows advertising storage and consent-dependent advertising requests.",
  PERSONALIZATION: "Allows optional personalized experiences, including personalized advertising when separately eligible."
});

export function normalizePrivacyConfig(raw = {}) {
  const profile = CONSENT_PROFILES.includes(raw.profile) ? raw.profile : "STRICT_OPT_IN";
  const categories = [...new Set((Array.isArray(raw.categories) ? raw.categories : CONSENT_CATEGORIES).filter(value => CONSENT_CATEGORIES.includes(value)))];
  if (!categories.includes("NECESSARY")) categories.unshift("NECESSARY");
  return {
    enabled: raw.enabled === true,
    policyVersion: String(raw.policyVersion || "").trim(),
    profile,
    categories,
    privacyPolicyUrl: String(raw.privacyPolicyUrl || "").trim(),
    cookiePolicyUrl: String(raw.cookiePolicyUrl || "").trim(),
    googleConsentEnabled: raw.googleConsentEnabled === true,
    prebidConsentEnabled: raw.prebidConsentEnabled === true
  };
}

export function initialCategoryState(_config) {
  return Object.fromEntries(CONSENT_CATEGORIES.map(category => [category, category === "NECESSARY" ? "GRANTED" : "UNSET"]));
}

export function resolveConsentState(configInput, stored, policyInput = {}) {
  const config = normalizePrivacyConfig(configInput);
  const validStored = stored && stored.publicationId === policyInput.publicationId && stored.policyVersion === config.policyVersion && stored.categories && typeof stored.categories === "object";
  const categories = initialCategoryState(config);
  if (validStored) {
    for (const category of OPTIONAL_CONSENT_CATEGORIES) {
      const value = config.categories.includes(category) ? stored.categories[category] : "UNSET";
      categories[category] = CONSENT_VALUES.includes(value) ? value : "UNSET";
    }
  }
  categories.NECESSARY = "GRANTED";
  if (policyInput.globalPrivacyControl === true && categories.PERSONALIZATION !== "DENIED") categories.PERSONALIZATION = "DENIED";
  const optional = OPTIONAL_CONSENT_CATEGORIES.map(category => categories[category]);
  const status = !validStored ? "UNKNOWN" : optional.every(value => value === "GRANTED") ? "ACCEPTED_ALL" : optional.every(value => value === "DENIED") ? "REJECTED_OPTIONAL" : "CUSTOM";
  return { status, categories, consentKnown: validStored, policyVersion: config.policyVersion, source: validStored ? "FIRST_PARTY_COOKIE" : "DEFAULT_FAIL_CLOSED", globalPrivacyControl: policyInput.globalPrivacyControl === true };
}

export function advertisingConsentContract(state) {
  return {
    advertisingAllowed: state?.consentKnown === true && state.categories?.ADVERTISING === "GRANTED",
    personalizedAdvertisingAllowed: state?.consentKnown === true && state.categories?.ADVERTISING === "GRANTED" && state.categories?.PERSONALIZATION === "GRANTED" && state.globalPrivacyControl !== true,
    analyticsAllowed: state?.consentKnown === true && state.categories?.ANALYTICS === "GRANTED",
    consentKnown: state?.consentKnown === true,
    policyVersion: state?.policyVersion || "",
    source: state?.source || "DEFAULT_FAIL_CLOSED"
  };
}

export function googleConsentSignals(state) {
  const contract = advertisingConsentContract(state);
  return {
    ad_storage: contract.advertisingAllowed ? "granted" : "denied",
    analytics_storage: contract.analyticsAllowed ? "granted" : "denied",
    ad_user_data: contract.advertisingAllowed ? "granted" : "denied",
    ad_personalization: contract.personalizedAdvertisingAllowed ? "granted" : "denied",
    functionality_storage: state?.consentKnown && state.categories?.FUNCTIONAL === "GRANTED" ? "granted" : "denied"
  };
}

export function prebidConsentBoundary(state) {
  const contract = advertisingConsentContract(state);
  return { eligible: contract.advertisingAllowed, consentKnown: contract.consentKnown, tcfString: null, gppString: null, source: contract.source };
}

export function customCodeAllowed(category, state) {
  return OPTIONAL_CONSENT_CATEGORIES.includes(category) && state?.consentKnown === true && state.categories?.[category] === "GRANTED";
}

export function consentCookieName(publicationId) {
  return `surgedome_consent_${String(publicationId || "unknown").replace(/[^a-zA-Z0-9]/g, "").slice(0, 24)}`;
}

export function encodeConsentCookie(value) {
  return encodeURIComponent(JSON.stringify(value));
}

export function decodeConsentCookie(value) {
  try {
    const parsed = JSON.parse(decodeURIComponent(value));
    if (!parsed || typeof parsed !== "object" || typeof parsed.publicationId !== "string" || typeof parsed.policyVersion !== "string" || typeof parsed.timestamp !== "string" || !parsed.categories || typeof parsed.categories !== "object") return null;
    return parsed;
  } catch { return null; }
}

export const FIRST_PARTY_COOKIE_REGISTRY = Object.freeze([
  { name: "admin_token", purpose: "Authenticated administrator session", category: "NECESSARY", scope: "platform", duration: "7 days", essential: true },
  { name: "surgedome_consent_<publication>", purpose: "Publication-scoped privacy preferences", category: "NECESSARY", scope: "publication", duration: "180 days", essential: true }
]);
